/** Shared raw-frame plumbing for the native video render paths.
 *
 *  Everything in here runs BOTH on the main thread
 *  (`raw-video-receiver.ts`, fallback painting) and inside the render
 *  worker (`raw-render.worker.ts`) — keep it free of DOM-only APIs,
 *  Tauri imports and the odin-common logger (whose [UI] bridge needs
 *  `window`). Deep GL diagnostics log via `console.warn`; release-
 *  relevant failures are reported by the callers through the bridged
 *  logger. */

/** Pixel format of a `RawPreviewFrame`. Mirrors `RawFrameFormat` in
 *  `src-tauri/plugins/odin/src/video/types.rs`. */
export enum RawFrameFormat {
  Bgra = 0,
  Nv12 = 1,
}

/** Wire format for the raw-preview channel — mirror of
 *  `RawPreviewFrameMsg` in `src-tauri/plugins/odin/src/video/types.rs`. The Rust
 *  side ships an `InvokeResponseBody::Raw` so each `onmessage` lands
 *  here as a plain `ArrayBuffer` (no JSON parse, no Uint8Array copy
 *  through a number-array intermediate). */
export interface RawPreviewFrame {
  width: number;
  height: number;
  timestamp_us: number;
  format: RawFrameFormat;
  /** Tightly-packed pixel bytes:
   *    - `Bgra`: `width * height * 4` (B, G, R, A interleaved)
   *    - `Nv12`: `width * height * 1.5` (Y plane + interleaved UV plane)
   *  Zero-copy view over the source ArrayBuffer; do not retain past
   *  the current paint cycle (the next frame replaces the buffer). */
  data: Uint8Array;
}

export const RAW_HEADER_LEN = 16;

export function decodeRawPreviewFrame(buf: ArrayBuffer): RawPreviewFrame {
  const view = new DataView(buf);
  const width = view.getUint16(0, true);
  const height = view.getUint16(2, true);
  const format = view.getUint8(4) as RawFrameFormat;
  // off 5..8 reserved (one u8 + one u16) so the pixel data lands
  // 8-byte aligned after the u64 timestamp.
  const timestamp_us = Number(view.getBigUint64(8, true));
  return {
    width,
    height,
    timestamp_us,
    format,
    data: new Uint8Array(buf, RAW_HEADER_LEN),
  };
}

/** Metadata posted alongside a WebView2 shared frame buffer — mirror
 *  of the JSON in `src-tauri/plugins/odin/src/video/shared_frame_buffer.rs`. */
export interface SharedFrameBufferMeta {
  token: string;
  slotCount: number;
  slotPayloadMax: number;
  globalHeaderLen: number;
  slotHeaderLen: number;
}

/** "NXVF" — must match `MAGIC` in `shared_frame_buffer.rs`. */
export const SHARED_MAGIC = 0x4e585646;
export const SHARED_VERSION = 1;

/** Poll one tick of the shared-buffer seqlock ring. Returns the new
 *  frame (a zero-copy view into `buffer`) or `null` when nothing new
 *  is published / the writer is mid-slot. `state.lastSeq` is advanced
 *  on a successful read; `state.tearCount` increments when the writer
 *  lapped the slot while the caller painted the PREVIOUS frame — call
 *  `verifySharedSlot` after painting for that check. */
export interface SharedRingState {
  buffer: ArrayBuffer;
  dv: DataView;
  meta: SharedFrameBufferMeta;
  lastSeq: number;
  tearCount: number;
  /** Slot offset of the most recently returned frame, for the
   *  post-paint seq verification. */
  lastSlotOff: number;
}

export function makeSharedRingState(
  buffer: ArrayBuffer,
  meta: SharedFrameBufferMeta,
): SharedRingState | null {
  const dv = new DataView(buffer);
  let headerOk = false;
  try {
    headerOk = dv.getUint32(0, true) === SHARED_MAGIC && dv.getUint32(4, true) === SHARED_VERSION;
  } catch {
    headerOk = false;
  }
  if (!headerOk) return null;
  return { buffer, dv, meta, lastSeq: 0, tearCount: 0, lastSlotOff: -1 };
}

export function pollSharedRing(s: SharedRingState): RawPreviewFrame | null {
  const latestSeq = s.dv.getUint32(16, true);
  if (latestSeq === 0 || latestSeq === s.lastSeq) return null;
  const slotIdx = s.dv.getUint32(20, true) % s.meta.slotCount;
  const stride = s.meta.slotHeaderLen + s.meta.slotPayloadMax;
  const slotOff = s.meta.globalHeaderLen + slotIdx * stride;
  // Seqlock entry check: the writer stamps the slot seq before the
  // payload — a mismatch means it's mid-write, try again next tick.
  if (s.dv.getUint32(slotOff, true) !== latestSeq) return null;
  const width = s.dv.getUint16(slotOff + 4, true);
  const height = s.dv.getUint16(slotOff + 6, true);
  const format = s.dv.getUint8(slotOff + 8) as RawFrameFormat;
  const payloadLen = s.dv.getUint32(slotOff + 12, true);
  const timestamp_us = Number(s.dv.getBigUint64(slotOff + 16, true));
  if (payloadLen === 0 || slotOff + s.meta.slotHeaderLen + payloadLen > s.buffer.byteLength) {
    return null;
  }
  s.lastSeq = latestSeq;
  s.lastSlotOff = slotOff;
  return {
    width,
    height,
    timestamp_us,
    format,
    data: new Uint8Array(s.buffer, slotOff + s.meta.slotHeaderLen, payloadLen),
  };
}

/** Seqlock exit check — call after painting the frame returned by
 *  `pollSharedRing`. Increments `tearCount` when the writer reused
 *  the slot mid-paint (the displayed frame was torn). */
export function verifySharedSlot(s: SharedRingState): void {
  if (s.lastSlotOff < 0) return;
  if (s.dv.getUint32(s.lastSlotOff, true) !== s.lastSeq) {
    s.tearCount += 1;
  }
}

/** Rolling per-second timing bucket. Mirror of the Rust-side
 *  `PerfBucket` in `dxva_decoder.rs` so the two log streams compare
 *  one-to-one when diagnosing pipeline stalls. */
export class PerfBucket {
  count = 0;
  totalMs = 0;
  maxMs = 0;
  record(ms: number): void {
    this.count += 1;
    this.totalMs += ms;
    if (ms > this.maxMs) this.maxMs = ms;
  }
  avgMs(): number {
    return this.count === 0 ? 0 : this.totalMs / this.count;
  }
  reset(): void {
    this.count = 0;
    this.totalMs = 0;
    this.maxMs = 0;
  }
}

/** Canvas surface both paint paths accept: a DOM `<canvas>` on the
 *  main thread, an `OffscreenCanvas` inside the render worker. The
 *  WebGL context object behaves identically on both. */
export type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;

const LOSS_HANDLED = Symbol('odin.glLossHandled');

interface CanvasWithLossHandler {
  [LOSS_HANDLED]?: boolean;
}

/** Wire the context-loss listeners once per canvas. Chromium caps
 *  live WebGL contexts (~16 per page) and evicts the *oldest* when a
 *  new one pushes past the cap — which is exactly a long-mounted
 *  gallery tile. `preventDefault()` on `webglcontextlost` is
 *  mandatory for the browser to ever fire `webglcontextrestored`;
 *  both events drop the cached GL state so the next paint rebuilds
 *  against the live context instead of feeding a dead one. */
function ensureLossHandlers(canvas: AnyCanvas): void {
  const holder = canvas as CanvasWithLossHandler & CanvasWithNv12 & CanvasWithBgra;
  if (holder[LOSS_HANDLED]) return;
  holder[LOSS_HANDLED] = true;
  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    holder[NV12_STATE] = undefined;
    holder[BGRA_STATE] = undefined;
  });
  canvas.addEventListener('webglcontextrestored', () => {
    holder[NV12_STATE] = undefined;
    holder[BGRA_STATE] = undefined;
  });
}

function getWebGl(canvas: AnyCanvas): WebGLRenderingContext | null {
  ensureLossHandlers(canvas);
  const gl = (canvas as HTMLCanvasElement).getContext('webgl', {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    preserveDrawingBuffer: false,
  }) as WebGLRenderingContext | null;
  if (gl?.isContextLost()) {
    // Either parked via `loseContext()` on a previous unmount (see
    // `clearCanvasGlState`) or evicted by the browser. Kick off a
    // restore and report unavailable — the `webglcontextrestored`
    // handler above clears the failure latch, so the next paint
    // rebuilds on the live context. (`restoreContext` after a
    // natural loss just sets a GL error — harmless.)
    gl.getExtension('WEBGL_lose_context')?.restoreContext();
    return null;
  }
  return gl;
}

/** Per-canvas WebGL state for the NV12 paint path. Lazy-built on
 *  first paint, retained across frames (texture handles, shader
 *  program, vertex buffer) so steady-state painting is just two
 *  `texSubImage2D` calls + a `drawArrays`.
 *
 *  Allocated once per `(canvas, dimensions)` pair — on a size change
 *  we re-create the textures so we can use `texImage2D` with the new
 *  dims and switch back to `texSubImage2D` from there. */
interface Nv12GlState {
  gl: WebGLRenderingContext;
  program: WebGLProgram;
  yTex: WebGLTexture;
  uvTex: WebGLTexture;
  yLocation: WebGLUniformLocation;
  uvLocation: WebGLUniformLocation;
  /** Dimensions the textures were last allocated for. Re-create when
   *  the source size changes (resolution switch mid-stream). */
  width: number;
  height: number;
}

/** Stored on a canvas via a private symbol so we can pick up the
 *  per-canvas WebGL state across frames without leaking it via a
 *  parallel map (which would require manual cleanup on unmount). */
const NV12_STATE = Symbol('odin.nv12GlState');

interface CanvasWithNv12 {
  [NV12_STATE]?: Nv12GlState | null;
}

/** Vertex shader: a single full-screen quad. Shared by the NV12 and
 *  BGRA paint paths. */
const QUAD_VERT_SRC = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  // Flip V because WebGL's clip-space y points up but image y points
  // down. Without this the image renders upside-down.
  v_uv.y = 1.0 - v_uv.y;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

/** Fragment shader: sample the Y plane (LUMINANCE) and the UV plane
 *  (LUMINANCE_ALPHA — U in the L channel, V in the A channel), then
 *  do the BT.709 limited-range YUV → RGB conversion. BT.709 limited
 *  is what every screen-share sender in the fleet emits (macOS SCK
 *  '420v', Windows MFT + Linux NVENC BGRA→NV12 converters) and the
 *  HD default browsers assume — BT.601 here shifted hues (pink LEDs
 *  rendered purple) on streams from native senders. */
const NV12_FRAG_SRC = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_y;
uniform sampler2D u_uv;
void main() {
  float y = texture2D(u_y, v_uv).r;
  vec2 uv = texture2D(u_uv, v_uv).ra; // .r = U, .a = V
  // BT.709 limited range:
  //   Y in [16/255, 235/255], U,V in [16/255, 240/255] mapped around 0.5
  float Y = (y - 16.0 / 255.0) * (255.0 / 219.0);
  float U = (uv.x - 128.0 / 255.0) * (255.0 / 224.0);
  float V = (uv.y - 128.0 / 255.0) * (255.0 / 224.0);
  float r = Y + 1.5748 * V;
  float g = Y - 0.1873 * U - 0.4681 * V;
  float b = Y + 1.8556 * U;
  gl_FragColor = vec4(clamp(r, 0.0, 1.0), clamp(g, 0.0, 1.0), clamp(b, 0.0, 1.0), 1.0);
}
`;

/** Fragment shader for the BGRA path: sample the frame uploaded as an
 *  RGBA texture (so the byte order lands as R←B, G←G, B←R) and swap
 *  the channels back on the GPU. Replaces a per-pixel JS swizzle loop
 *  (~2M iterations/frame at 1080p) + `putImageData` copy. Source
 *  alpha is meaningless from screencast (often 0) — force opaque. */
const BGRA_FRAG_SRC = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_tex;
void main() {
  vec4 c = texture2D(u_tex, v_uv);
  gl_FragColor = vec4(c.b, c.g, c.r, 1.0);
}
`;

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn('[raw-preview] shader compile failed:', gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

/** Compile + link the shared full-screen-quad vertex shader with the
 *  given fragment shader, bind the program, and wire the `a_pos`
 *  quad attribute. Returns the ready-to-draw program or `null`. */
function buildQuadProgram(gl: WebGLRenderingContext, fragSrc: string): WebGLProgram | null {
  const vs = compileShader(gl, gl.VERTEX_SHADER, QUAD_VERT_SRC);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc);
  if (!vs || !fs) return null;
  const program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.warn('[raw-preview] program link failed:', gl.getProgramInfoLog(program));
    return null;
  }
  gl.useProgram(program);

  // Full-screen quad — two triangles, four vertices.
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const posLoc = gl.getAttribLocation(program, 'a_pos');
  gl.enableVertexAttribArray(posLoc);
  gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
  return program;
}

function buildNv12State(canvas: AnyCanvas, width: number, height: number): Nv12GlState | null {
  const gl = getWebGl(canvas);
  if (!gl) return null;
  const program = buildQuadProgram(gl, NV12_FRAG_SRC);
  if (!program) return null;

  const yLocation = gl.getUniformLocation(program, 'u_y');
  const uvLocation = gl.getUniformLocation(program, 'u_uv');
  if (!yLocation || !uvLocation) return null;

  // Pixel store: tightly-packed bytes — disable the default 4-byte
  // row alignment so odd-stride Y / UV uploads land row-aligned.
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

  const yTex = gl.createTexture();
  const uvTex = gl.createTexture();
  if (!yTex || !uvTex) return null;

  for (const tex of [yTex, uvTex]) {
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  // Pre-allocate at the full size; subsequent frames use texSubImage2D.
  gl.bindTexture(gl.TEXTURE_2D, yTex);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.LUMINANCE,
    width,
    height,
    0,
    gl.LUMINANCE,
    gl.UNSIGNED_BYTE,
    null,
  );
  gl.bindTexture(gl.TEXTURE_2D, uvTex);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.LUMINANCE_ALPHA,
    width / 2,
    height / 2,
    0,
    gl.LUMINANCE_ALPHA,
    gl.UNSIGNED_BYTE,
    null,
  );

  return { gl, program, yTex, uvTex, yLocation, uvLocation, width, height };
}

/** Per-canvas WebGL state for the BGRA paint path — one RGBA texture
 *  plus the channel-swap program. Same lifecycle as `Nv12GlState`. */
interface BgraGlState {
  gl: WebGLRenderingContext;
  program: WebGLProgram;
  tex: WebGLTexture;
  texLocation: WebGLUniformLocation;
  width: number;
  height: number;
}

const BGRA_STATE = Symbol('odin.bgraGlState');

interface CanvasWithBgra {
  /** `undefined` = not initialized / retry after a transient WebView
   *  compositor loss, `null` retained for older canvas instances. */
  [BGRA_STATE]?: BgraGlState | null;
}

function buildBgraState(canvas: AnyCanvas, width: number, height: number): BgraGlState | null {
  const gl = getWebGl(canvas);
  if (!gl) return null;
  const program = buildQuadProgram(gl, BGRA_FRAG_SRC);
  if (!program) return null;
  const texLocation = gl.getUniformLocation(program, 'u_tex');
  if (!texLocation) return null;

  const tex = gl.createTexture();
  if (!tex) return null;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  // Pre-allocate at the full size; subsequent frames use texSubImage2D.
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

  return { gl, program, tex, texLocation, width, height };
}

/** GPU path for BGRA frames: upload the raw bytes as an RGBA texture
 *  and let the fragment shader swap the channels. Returns `false`
 *  when WebGL isn't available on this canvas (the main-thread caller
 *  falls back to the CPU swizzle + 2D canvas path; the worker skips
 *  and reports).
 *
 *  `useProgram` runs per paint: the render worker composites every
 *  stream through one shared context, so the previous paint may well
 *  have left the other format's program bound. */
export function paintBgraGl(canvas: AnyCanvas, frame: RawPreviewFrame): boolean {
  const holder = canvas as CanvasWithBgra;
  let state = holder[BGRA_STATE];
  // GL calls on a lost context are silent no-ops — without this check
  // the paint "succeeds" forever while the tile stays blank.
  if (state && state.gl.isContextLost()) {
    holder[BGRA_STATE] = state = undefined;
  }
  if (state === null) {
    holder[BGRA_STATE] = state = undefined;
  }
  if (!state || state.width !== frame.width || state.height !== frame.height) {
    state = buildBgraState(canvas, frame.width, frame.height) ?? undefined;
    holder[BGRA_STATE] = state;
    if (!state) return false;
  }
  const { gl, program, tex, texLocation, width, height } = state;
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  gl.viewport(0, 0, width, height);
  gl.useProgram(program);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, frame.data);
  gl.uniform1i(texLocation, 0);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  return true;
}

/** GPU path for NV12 frames. Returns `false` while WebGL is unavailable.
 *
 * Unlike the BGRA path, NV12 has no 2D-canvas fallback, so an init
 * failure must not be latched permanently. Android WebView can lose
 * the canvas context while a full-screen backdrop-filter (for example
 * Connection Stats) is composited; rebuilding on later frames lets the
 * preview recover even when that WebView omits `webglcontextrestored`. */
export function paintNv12(canvas: AnyCanvas, frame: RawPreviewFrame): boolean {
  const holder = canvas as CanvasWithNv12;
  let state = holder[NV12_STATE];
  // GL calls on a lost context are silent no-ops — without this check
  // the paint "succeeds" forever while the tile stays blank.
  if (state && state.gl.isContextLost()) {
    holder[NV12_STATE] = state = undefined;
  }
  if (state === null) {
    holder[NV12_STATE] = state = undefined;
  }
  if (!state || state.width !== frame.width || state.height !== frame.height) {
    state = buildNv12State(canvas, frame.width, frame.height) ?? undefined;
    holder[NV12_STATE] = state;
    if (!state) return false;
  }
  const { gl, program, yTex, uvTex, yLocation, uvLocation, width, height } = state;
  // Same reason as the BGRA path: the compositor context is shared
  // between formats, so bind this program before every draw instead of
  // relying on the one bound when the state was built.
  gl.useProgram(program);
  // Ensure the GL viewport matches the canvas backing — the
  // <canvas> element's width/height attributes might have been
  // resized between frames if a CSS layout pass touched them.
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  gl.viewport(0, 0, width, height);

  // Y plane = first width*height bytes.
  const yLen = width * height;
  const yView = new Uint8Array(frame.data.buffer, frame.data.byteOffset, yLen);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, yTex);
  gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, width, height, gl.LUMINANCE, gl.UNSIGNED_BYTE, yView);
  gl.uniform1i(yLocation, 0);

  // UV plane = next width*height/2 bytes (interleaved U,V samples
  // — 2 bytes per chroma sample at half resolution).
  const uvView = new Uint8Array(frame.data.buffer, frame.data.byteOffset + yLen, yLen / 2);
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, uvTex);
  gl.texSubImage2D(
    gl.TEXTURE_2D,
    0,
    0,
    0,
    width / 2,
    height / 2,
    gl.LUMINANCE_ALPHA,
    gl.UNSIGNED_BYTE,
    uvView,
  );
  gl.uniform1i(uvLocation, 1);

  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  return true;
}

/** Drop the per-canvas GL state stashes so GPU resources release
 *  sooner and a remount rebuilds cleanly. `undefined` (not `null`) so
 *  a remount rebuilds instead of reading the "init failed" latch.
 *
 *  Also releases the WebGL context itself via `loseContext()`:
 *  Chromium keeps evicting the *oldest* live context once ~16 exist,
 *  and unmounted canvases waiting for GC counted against that cap —
 *  enough fullscreen/tab churn and the browser started killing the
 *  contexts of long-mounted gallery tiles (blank tiles after leaving
 *  fullscreen). A remount of the same canvas recovers through
 *  `getWebGl`'s restore path. */
export function clearCanvasGlState(canvas: AnyCanvas): void {
  const gl =
    (canvas as CanvasWithNv12)[NV12_STATE]?.gl ?? (canvas as CanvasWithBgra)[BGRA_STATE]?.gl;
  if (gl && !gl.isContextLost()) {
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
  (canvas as CanvasWithNv12)[NV12_STATE] = undefined;
  (canvas as CanvasWithBgra)[BGRA_STATE] = undefined;
}
